import React, { useState, useEffect } from 'react';
import { POSILA_PRIJEMCI } from '../../../utils/posilaPrijemci';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    FormControl,
    FormControlLabel,
    InputLabel,
    Select,
    MenuItem,
    Switch,
} from '@mui/material';
import type { Konfigurace } from '../../../api/JsdhNotifikaceService/generated';
import type { useUpdateAdminKonfiguraceMutation } from '../../../api/JsdhNotifikaceService/service/AdminMutations';

interface NotifikaceTabProps {
    konfigurace: Konfigurace[] | undefined;
    updateKonfiguraceMutation: ReturnType<typeof useUpdateAdminKonfiguraceMutation>;
    refetchKonfigurace: () => void;
}

const NotifikaceTab: React.FC<NotifikaceTabProps> = ({
    konfigurace,
    updateKonfiguraceMutation,
    refetchKonfigurace,
}) => {
    const getVal = (key: string) => konfigurace?.find(k => k.klic === `POSILOVA_TECHNIKA_${key}`)?.hodnota;

    const pushEnabled = getVal('PUSH_ENABLED') !== '0';
    const pushDisabled = !pushEnabled || updateKonfiguraceMutation.isPending;

    const [pushText, setPushText] = useState(getVal('PUSH_TEXT') || '');
    const [pushTextDirty, setPushTextDirty] = useState(false);

    const [souhrnMinuty, setSouhrnMinuty] = useState(
        konfigurace?.find(k => k.klic === 'EXTERNI_SOUHRN_MINUTY')?.hodnota || '15'
    );
    const [souhrnDirty, setSouhrnDirty] = useState(false);

    useEffect(() => {
        const val = getVal('PUSH_TEXT') || '';
        setPushText(val);
        setPushTextDirty(false);
    }, [konfigurace]);

    useEffect(() => {
        const val = konfigurace?.find(k => k.klic === 'EXTERNI_SOUHRN_MINUTY')?.hodnota || '15';
        setSouhrnMinuty(val);
        setSouhrnDirty(false);
    }, [konfigurace]);

    const handleSwitchChange = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        updateKonfiguraceMutation.mutate(
            { klic: `POSILOVA_TECHNIKA_${key}`, hodnota: e.target.checked ? '1' : '0' },
            { onSuccess: () => refetchKonfigurace() }
        );
    };

    const handleSavePushText = () => {
        updateKonfiguraceMutation.mutate(
            { klic: 'POSILOVA_TECHNIKA_PUSH_TEXT', hodnota: pushText },
            { onSuccess: () => { refetchKonfigurace(); setPushTextDirty(false); } }
        );
    };

    return (
        <>
        <Paper sx={{ mb: 3, p: 2 }}>
            <Typography variant="h6" gutterBottom>
                Posilová technika – emailový poplach
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Nastavení chování při příjmu emailu s další technikou k existujícímu poplachu.
            </Typography>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Push notifikace</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 1 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={pushEnabled}
                            onChange={handleSwitchChange('PUSH_ENABLED')}
                            disabled={updateKonfiguraceMutation.isPending}
                        />
                    }
                    label="Povolit push notifikace"
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={getVal('KRITICKA_NOTIFIKACE') !== '0'}
                            onChange={handleSwitchChange('KRITICKA_NOTIFIKACE')}
                            disabled={pushDisabled}
                        />
                    }
                    label="Kritická notifikace"
                />
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', maxWidth: 500 }}>
                    <TextField
                        label="Text notifikace"
                        size="small"
                        fullWidth
                        value={pushText}
                        onChange={(e) => { setPushText(e.target.value); setPushTextDirty(true); }}
                        disabled={pushDisabled}
                    />
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleSavePushText}
                        disabled={pushDisabled || !pushTextDirty || updateKonfiguraceMutation.isPending}
                        sx={{ whiteSpace: 'nowrap', mt: 0.5 }}
                    >
                        Uložit
                    </Button>
                </Box>
                <FormControl size="small" sx={{ maxWidth: 300 }} disabled={pushDisabled}>
                    <InputLabel>Příjemci</InputLabel>
                    <Select
                        value={getVal('PUSH_PRIJEMCI') || 'ALL'}
                        label="Příjemci"
                        onChange={(e) => {
                            updateKonfiguraceMutation.mutate(
                                { klic: 'POSILOVA_TECHNIKA_PUSH_PRIJEMCI', hodnota: e.target.value },
                                { onSuccess: () => refetchKonfigurace() }
                            );
                        }}
                    >
                        {POSILA_PRIJEMCI.map((p) => (
                            <MenuItem key={p.value} value={p.value}>{p.menuLabel}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
        </Paper>

        <Paper sx={{ mb: 3, p: 2 }}>
            <Typography variant="h6" gutterBottom>
                Externí oznámení (starosta / rodina)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Souhrn s potvrzenými členy se posílá externím kontaktům (typ Starosta/Jiný)
                za nastavený počet minut od vyhlášení poplachu. Kontakty: Jednotka → Externí kontakty.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', maxWidth: 360 }}>
                <TextField
                    label="Souhrn po (minutách)"
                    size="small"
                    type="number"
                    value={souhrnMinuty}
                    onChange={(e) => { setSouhrnMinuty(e.target.value); setSouhrnDirty(true); }}
                    inputProps={{ min: 1, max: 240 }}
                />
                <Button
                    variant="contained" size="small"
                    onClick={() => updateKonfiguraceMutation.mutate(
                        { klic: 'EXTERNI_SOUHRN_MINUTY', hodnota: souhrnMinuty },
                        { onSuccess: () => { refetchKonfigurace(); setSouhrnDirty(false); } }
                    )}
                    disabled={!souhrnDirty || updateKonfiguraceMutation.isPending}
                    sx={{ whiteSpace: 'nowrap', mt: 0.5 }}
                >
                    Uložit
                </Button>
            </Box>
        </Paper>
        </>
    );
};

export default NotifikaceTab;
